<template>
  <div class="data-section overview-data">
    <v-data-table class="data-table" :headers="headers" :items="visits" :expanded.sync="expanded" show-expand item-key="id" :item-style="isEmptyDentistStyle" :loading-text="loadingText" loader-height="2" :loading="tableLoading" hide-default-footer :mobile-breakpoint="1200" :server-items-length="totalEntities" :sort-by="defaultOrderBy" :sort-desc="defaultOrderByDesc" @update:options="serverSort">
      <template v-slot:item.patientName="{ item }">
        <span>{{ item.patientFirstName ? item.patientFirstName.trim() : '..' }}</span>
        <span>{{ item.patientLastName ? ` ${item.patientLastName.trim()}` : ' ..' }}</span>
      </template>
      <template v-slot:item.patientAge="{ item }">
        <span>{{ item.patientAge ? item.patientAge : '--' }}</span>
      </template>
      <template v-slot:item.createDate="{ item }">
        <DateCell :date="item.createDate"/>
      </template>
      <template v-slot:item.id="{ item }">
        <div class="d-flex align-center">
          <template v-if="item.externalId">
            <span class="text-no-wrap">{{ item.id }}</span>
            <v-tooltip bottom :allow-overflow="true">
              <template v-slot:activator="{ on, attrs }">
                <v-icon size="21" class="ml-2" color="#48aa7b" v-bind="attrs" v-on="on">icon-edi</v-icon>
              </template>
              <span>EDI: {{ item.externalId }}</span>
            </v-tooltip>
          </template>
          <span v-else>{{ item.id }}</span>
        </div>
      </template>
      <template v-slot:item.status="{ item }">
        <v-select class="my-0 py-0 overview-data__clear-select" v-model="item.status" @change="resetUpdateButton(item); isFinished(item)" :items="statusTypes" item-text="label" item-value="key" single-line hide-details :append-icon="selectIcon" :readonly="isDentist || isSecretary">
          <template v-slot:selection="{ item, index }">
            <span :class="[item.color, 'status-label', 'mr-2']"/>
            <span class="text-body-2 text-truncate d-block" style="max-width: 130px">{{ item.label }}</span>
          </template>
          <template v-slot:item="{ item, index }">
            <span :class="[item.color, 'status-label', 'mr-2']"/> <span class="text-body-2">{{ item.label }}</span>
          </template>
        </v-select>
      </template>
      <template v-slot:item.save="{ item }">
        <v-btn v-if="!item.isExpandedRow" depressed class="mb-2 mb-md-0 px-2" min-width="0" :color="item.updateButton.color" :loading="item.updateButton.loading" :disabled="item.updateButton.loading || isDentist || isSecretary" @click="updateVisitStatus(item)">GEM</v-btn>
      </template>
      <template v-slot:item.isExport="{ item }">
        <OverviewExportIcon :visitItem="item" @updateCommonVisitInfo="editCommonVisitInfo(item, $event)" />
      </template>
      <template v-slot:item.visitOnPause="{ item }">
        <v-tooltip bottom :allow-overflow="true">
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-if="item.visitOnPause" v-bind="attrs" v-on="on" size="23">mdi-pause-circle</v-icon>
          </template>
          <span>Pausesat indtil {{ pauseEndDateValue(item.pauseEndDate) }}</span>
        </v-tooltip>
      </template>
      <template v-slot:item.haveUnansweredMessage="{ item }">
        <v-tooltip bottom :allow-overflow="true">
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-if="item.haveUnansweredMessage && item.surgeonHaveUnreadMessages" v-bind="attrs" v-on="on" size="23" color="#4494A8">mdi-message-processing</v-icon>
            <v-icon v-else-if="item.haveUnansweredMessage" v-bind="attrs" v-on="on" size="23" class="red--text text--lighten-2">mdi-message-processing</v-icon>
          </template>
          <span v-if="item.haveUnansweredMessage && item.surgeonHaveUnreadMessages">Mine nye beskeder</span>
          <span v-else-if="item.haveUnansweredMessage">Nye beskeder</span>
        </v-tooltip>
      </template>
      <template v-slot:item.referenceType="{ item }">
        <v-tooltip bottom :allow-overflow="true">
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-if="item.referenceType === 1" v-bind="attrs" v-on="on" size="20" class="red--text">icon-high-priority</v-icon>
          </template>
          <span>Haste henvisning</span>
        </v-tooltip>
      </template>
      <template v-slot:item.radiographFiles="{ item }">
        <OverviewRadiographsIcon v-if="item.isRadiographUploaded" :visitItem="item" @changeExportStatus="changeExportStatus(item)"/>
      </template>
      <template v-slot:item.isPdfFile="{ item }">
        <span v-if="item.isPdfFile">
          <v-tooltip bottom :allow-overflow="true">
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" @click="downloadVisitPdf(item.id)" size="27">icon-pdf</v-icon>
            </template>
            <span>Download henvisningen som PDF her</span>
          </v-tooltip>
        </span>
      </template>
      <template v-slot:item.data-table-expand="{ item, expand, isExpanded }">
        <v-icon size="24" :class="[isExpanded && 'v-data-table__expand-icon--active']" @click="toggleExpand(item, expand, isExpanded)">icon-chevron-down</v-icon>
      </template>
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" style="height: auto;width: 1110px;" class="px-0 surgeon-journals-expanded-cell" @click.stop>
          <SurgeonJournalExpanded
            :visit-item="item"
            :key="`journal-${item.id}`"
            @updateCommonVisitInfo="editCommonVisitInfo(item, $event)"
            @updateVisitItem="editCommonVisitInfo(item, $event)"
          />
        </td>
      </template>
    </v-data-table>
    <v-pagination color="#e0e6ec" v-model="queryParams.CurrentPage" :length="totalPages" :total-visible="7" :disabled="tableLoading" class="py-4" @input="changePageNumber"/>
    <v-dialog v-model="dialog" persistent max-width="550" overlay-color="#fff" :overlay-opacity="0.7">
      <v-card>
        <v-card-title class="headline mb-4">Vil du slette vedhæftede filer, røntgen mm?</v-card-title>
        <v-divider/>
        <v-card-actions>
          <v-spacer/>
          <v-btn depressed color='primary' @click="isDeleteAttachmentsChange(true)">Ja, slet alle filerne</v-btn>
          <v-btn color="black darken-1" text @click="isDeleteAttachmentsChange(false)">Nej behold filerne</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import methods from '../../utils/methods';
  import api from '../../api/api';
  import SurgeonJournalExpanded from './SurgeonJournalExpanded';
  import OverviewRadiographsIcon from '../overview/OverviewRadiographsIcon';
  import DateCell from '../common/DateCell';
  import OverviewExportIcon from '@/components/overview/OverviewExportIcon';

  export default {
    name: 'SurgeonJournalsData',

    components: {
      OverviewExportIcon,
      DateCell,
      OverviewRadiographsIcon,
      SurgeonJournalExpanded,
    },

    data: function () {
      return {
        visits: [],
        expanded: [],
        loadingText: 'Loading... Please wait',
        dialog: false,
        dialogVisitId: null,
        defaultOrderBy: 'createDate',
        defaultOrderByDesc: true,
        totalEntities: 0,
        totalPages: 0,
        queryParams: {
          OrderBy: null,
          OrderByAscending: null,
          CurrentPage: 1,
          PageSize: 20,
        },
      };
    },

    computed: {
      isAdmin: function () {
        return this.$store.getters.isAdmin;
      },
      isSecretary: function () {
        return this.$store.getters.isSecretary;
      },
      isDentist: function () {
        return this.$store.getters.isDentist;
      },
      headers: function () {
        return [
          {text: 'Nr.', value: 'id', sortable: true, width: '9%'},
          {text: 'CPR-nummer', value: 'cpr', width: '12%'},
          {text: 'Patient', value: 'patientName', width: '12%'},
          {text: 'Alder', value: 'patientAge', width: '7%'},
          {text: 'Dato', value: 'createDate', width: '9%'},
          {text: 'Status', value: 'status', width: '200px'},
          {value: 'save', sortable: false, width: '80px', align: 'center'},
          {value: 'isExport', sortable: false, width: '45px', align: 'center'},
          {value: 'visitOnPause', sortable: false, width: '45px', align: 'center'},
          {value: 'haveUnansweredMessage', sortable: false, width: '45px', align: 'center'},
          {value: 'referenceType', sortable: false, width: '45px', align: 'center'},
          {value: 'radiographFiles', sortable: false, width: '45px', align: 'center'},
          {value: 'isPdfFile', sortable: false, width: '45px', align: 'center'},
          {value: 'data-table-expand', sortable: false, width: '44px'},
        ];
      },
      statusTypes: function () {
        return this.$store.state.overview.statusTypes;
      },
      tableLoading: function () {
        return this.$store.state.surgeonJournals.tableLoading;
      },
      filterParams: function () {
        return this.$store.state.surgeonJournals.filterParams;
      },
      selectIcon: function () {
        return this.isAdmin ? 'mdi-menu-down' : '';
      },
    },

    watch: {
      filterParams: function () {
        this.queryParams.CurrentPage = 1;
        this.getVisits();
      },
    },

    methods: {
      pauseEndDateValue: function (date) {
        if (date) {
          return `${methods.getDateFromIso(date)}, ${methods.getTimeFromIso(date)}`;
        }
        return '...';
      },
      isEmptyDentistStyle: function () {
        return null;
      },
      downloadVisitPdf: function (visitId) {
        methods.downloadURL(api.visitFileUrl(visitId));
      },
      resetUpdateButton: function (item) {
        item.updateButton.color !== 'primary' && (item.updateButton.color = 'primary');
      },
      isFinished: function (item) {
        if (item.status === 7) {
          this.dialog = true;
          this.dialogVisitId = item.id;
        }
      },
      isDeleteAttachmentsChange: function (flag) {
        let index = this.visits.findIndex((item) => item.id === this.dialogVisitId);
        this.visits[index].isDeleteAttachments = flag;
        this.dialog = false;
        this.dialogVisitId = null;
      },
      sortOrderValue: function (val) {
        switch (val.sortBy[0]) {
          case 'id':
            return 1;
          case 'cpr':
            return 2;
          case 'patientName':
            return 3;
          case 'patientAge':
            return 4;
          case 'createDate':
            return 5;
          case 'status':
            return 7;
          default:
            return 0;
        }
      },
      serverSort: function (val) {
        this.queryParams.OrderBy = this.sortOrderValue(val);
        this.queryParams.OrderByAscending = !val.sortDesc[0];
        this.getVisits();
      },
      toggleExpand: function (item, expand, isExpanded) {
        expand(!isExpanded);
        item.isExpandedRow = !isExpanded;
      },
      collapseVisit: function (visitItem, data) {
        visitItem.updateButton.color = data.buttonColor;
      },
      editCommonVisitInfo: function (visitItem, newVisitItem) {
        visitItem.externalId = newVisitItem.externalId;
        visitItem.status = newVisitItem.status;
        visitItem.cpr = newVisitItem.cpr;
        visitItem.radiographExportStatus = newVisitItem.radiographExportStatus;
        visitItem.isRadiographUploaded = newVisitItem.isRadiographUploaded;
        visitItem.isDeleteAttachments = false;
        visitItem.haveUnansweredMessage = newVisitItem.haveUnansweredMessage;
        visitItem.exportedToCsv = newVisitItem.exportedToCsv;
        visitItem.externalDoctorId = newVisitItem.externalDoctorId;
      },
      getVisits: async function () {
        try {
          this.visits = [];
          this.expanded = [];
          this.$store.commit('surgeonJournals/updateTableLoading', true);
          const OPERATION_DATE_ORDER_BY = 8;
          const isOperationDateFilterSelected =
            !!this.filterParams.OperationDateFrom || !!this.filterParams.OperationDateTo;
          const requestParams = {
            ...this.queryParams,
            ...this.filterParams,
            OrderBy: isOperationDateFilterSelected ? OPERATION_DATE_ORDER_BY : this.queryParams.OrderBy,
          };
          let {data} = await api.getVisits(requestParams);
          let entities = data.paginationResult.entities;
          entities.forEach((item) => {
            item.isDeleteAttachments = false;
            item.isExpandedRow = false;
            item.updateButton = {
              loading: false,
              color: 'primary',
            };
          });
          this.totalPages = Math.ceil(
            data.paginationResult.totalEntities / this.queryParams.PageSize,
          );
          this.visits = entities;
          this.totalEntities = data.paginationResult.totalEntities;
          this.$store.commit('surgeonJournals/updateTableLoading', false);
        } catch (e) {
          this.visits = [];
          this.expanded = [];
          this.$store.commit('surgeonJournals/updateTableLoading', false);
        }
      },
      updateVisitStatus: async function (item) {
        let newData = [
          {
            op: 'replace',
            path: '/status',
            value: item.status,
          },
        ];
        let params = {};
        item.isDeleteAttachments && (params.isDeleteAttachments = item.isDeleteAttachments);
        try {
          item.updateButton.loading = true;
          await api.updateVisitPatch(newData, item.id, params);
          let newVisit = await api.getVisits({VisitNumber: item.id});
          this.editCommonVisitInfo(item, newVisit.data.paginationResult.entities[0]);
          item.updateButton.loading = false;
          item.updateButton.color = 'success';
        } catch (e) {
          item.updateButton.loading = false;
          item.updateButton.color = 'error';
        }
      },
      changePageNumber: function (value) {
        this.queryParams.CurrentPage = value;
        this.getVisits();
      },
      changeExportStatus: function (item) {
        item.radiographExportStatus = 1;
      },
    },
  };
</script>

<style lang="scss" scoped>
  ::v-deep .v-data-table__expanded__content {
    pointer-events: auto;
  }

  ::v-deep .v-data-table__expanded__content .visit-appointments-table tbody tr,
  ::v-deep .v-data-table__expanded__content .visit-appointments-table tbody tr:hover,
  ::v-deep .v-data-table__expanded__content .visit-appointments-table tbody tr:active,
  ::v-deep .v-data-table__expanded__content .visit-appointments-table tbody tr td,
  ::v-deep .v-data-table__expanded__content .visit-appointments-table tbody tr:hover td {
    background: transparent !important;
    background-color: transparent !important;
    box-shadow: none !important;
    cursor: default !important;
    transition: none !important;
  }

  ::v-deep .v-data-table__expanded__content .visit-appointments-table table {
    pointer-events: none;
  }
</style>
